import { CalendarClock, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/utils/cn'
import { formatDay, formatRange, scheduleSummary } from '../format'
import { STATUS_LABEL, STATUS_TONE } from '../status'
import type { CourseRun } from '../types'

/** Runs as cards: course, group, dates, weekly sessions, progress and the next session. */
export function RunList({
  runs,
  compact = false,
  emptyText,
  hrefFor = (r) => `/admin/courses/runs/${r.id}`,
  showCourse = true,
}: {
  runs: CourseRun[]
  compact?: boolean
  emptyText: string
  hrefFor?: ((r: CourseRun) => string) | null
  showCourse?: boolean
}) {
  const { t } = useTranslation('courses')
  if (runs.length === 0) return <p className="text-sm text-muted">{emptyText}</p>
  return (
    <ul className={cn('grid gap-3', !compact && 'sm:grid-cols-2 xl:grid-cols-3')}>
      {runs.map((r) => {
        const body = (
          <>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-ink">
                  {showCourse ? r.course.name : r.section.label}
                </p>
                <p className="truncate text-xs text-muted">
                  {showCourse ? r.section.label : r.course.name}
                  {r.teacher ? ` · ${r.teacher.name}` : ''}
                </p>
              </div>
              <Badge tone={STATUS_TONE[r.status]} size="sm">
                {t(STATUS_LABEL[r.status])}
              </Badge>
            </div>
            <p className="mt-2 text-xs text-ink-soft">{formatRange(r.startsOn, r.endsOn)}</p>
            <p className="text-xs text-muted">{scheduleSummary(r.slots)}</p>
            {!compact ? (
              <>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-sunken" aria-hidden="true">
                  <div
                    className="h-full rounded-full bg-brand-500"
                    style={{ width: `${r.sessionCount ? (r.sessionsDone / r.sessionCount) * 100 : 0}%` }}
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-soft">
                  <span>
                    {t('{{done}} of {{total}} sessions', { done: r.sessionsDone, total: r.sessionCount })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Users className="size-3.5 text-muted" aria-hidden="true" />
                    {t('{{count}} students', { count: r.learnerCount })}
                  </span>
                </div>
                {r.nextSession ? (
                  <p className="mt-2 inline-flex items-center gap-1.5 rounded-control bg-brand-50 px-2 py-1 text-xs font-medium text-brand-800">
                    <CalendarClock className="size-3.5" aria-hidden="true" />
                    {t('Next: {{when}}', {
                      when: `${formatDay(r.nextSession.date)} ${r.nextSession.startsAt}–${r.nextSession.endsAt}`,
                    })}
                    {r.nextSession.room ? ` · ${r.nextSession.room}` : ''}
                  </p>
                ) : null}
              </>
            ) : null}
          </>
        )
        return (
          <li key={r.id}>
            {hrefFor ? (
              <Link
                to={hrefFor(r)}
                className="block h-full rounded-card border border-line bg-surface p-4 shadow-card transition hover:border-brand-300 hover:shadow-raised"
              >
                {body}
              </Link>
            ) : (
              <div className="h-full rounded-card border border-line bg-surface p-4 shadow-card">{body}</div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
